import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, ArrowLeft, Plus, Trash2, Upload, Image as ImageIcon,
  MapPin, Calendar, Users, DollarSign, Tag, FileText, List
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const categories = ['Domestic', 'International', 'Honeymoon', 'Corporate', 'Adventure', 'Custom'];

const emptyTour = {
  name: '',
  slug: '',
  destination: '',
  category: 'Domestic',
  description: '',
  short_description: '',
  duration_days: 3,
  duration_nights: 2,
  price: 0,
  discounted_price: null,
  max_group_size: 20,
  min_age: 0,
  difficulty: 'easy',
  images: [],
  highlights: [''],
  inclusions: [''],
  exclusions: [''],
  itinerary: [{ day: 1, title: '', description: '', meals: [], accommodation: '' }],
  status: 'active',
  featured: false,
  rating: 4.5,
  review_count: 0
};

export default function TourForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [tour, setTour] = useState(emptyTour);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (isEditing) {
      loadTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTour = async () => {
    try {
      const response = await adminApi.getTour(id);
      const data = response.data;
      // Ensure arrays have at least one empty item for the form
      setTour({
        ...emptyTour,
        ...data,
        highlights: data.highlights?.length ? data.highlights : [''],
        inclusions: data.inclusions?.length ? data.inclusions : [''],
        exclusions: data.exclusions?.length ? data.exclusions : [''],
        itinerary: data.itinerary?.length ? data.itinerary : [{ day: 1, title: '', description: '', meals: [], accommodation: '' }]
      });
    } catch (error) {
      console.error('Error loading tour:', error);
      toast.error('Failed to load tour');
      navigate('/admin/tours');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleChange = (field, value) => {
    setTour(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug when name changes
      if (field === 'name' && !isEditing) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleArrayChange = (field, index, value) => {
    setTour(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field, defaultValue = '') => {
    setTour(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setTour(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleItineraryChange = (index, key, value) => {
    setTour(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === index ? { ...day, [key]: value } : day
      )
    }));
  };

  const addItineraryDay = () => {
    setTour(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { 
        day: prev.itinerary.length + 1, 
        title: '', 
        description: '', 
        meals: [], 
        accommodation: '' 
      }]
    }));
  };

  const removeItineraryDay = (index) => {
    setTour(prev => ({
      ...prev,
      itinerary: prev.itinerary
        .filter((_, i) => i !== index)
        .map((day, i) => ({ ...day, day: i + 1 }))
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // For now, create preview URLs (in production, upload to server)
    const newImages = files.map(file => URL.createObjectURL(file));
    setTour(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
    toast.success(`${files.length} image(s) added`);
  };

  const removeImage = (index) => {
    setTour(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!tour.name.trim()) {
      toast.error('Tour name is required');
      return;
    }
    if (!tour.destination.trim()) {
      toast.error('Destination is required');
      return;
    }
    if (tour.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      // Clean up empty values
      const cleanedTour = {
        ...tour,
        highlights: tour.highlights.filter(h => h.trim()),
        inclusions: tour.inclusions.filter(i => i.trim()),
        exclusions: tour.exclusions.filter(e => e.trim()),
        itinerary: tour.itinerary.filter(d => d.title.trim() || d.description.trim()),
        discounted_price: tour.discounted_price || null
      };

      if (isEditing) {
        await adminApi.updateTour(id, cleanedTour);
        toast.success('Tour updated successfully');
      } else {
        await adminApi.createTour(cleanedTour);
        toast.success('Tour created successfully');
      }
      navigate('/admin/tours');
    } catch (error) {
      console.error('Error saving tour:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tour');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'details', label: 'Details', icon: List },
    { id: 'itinerary', label: 'Itinerary', icon: Calendar },
    { id: 'images', label: 'Images', icon: ImageIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" />
      </div>
    );
  }

  return (
    <div data-testid="tour-form">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/tours')}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              {isEditing ? 'Edit Tour' : 'Add New Tour'}
            </h1>
            <p className="text-[#4A4A4A]">
              {isEditing ? 'Update tour package details' : 'Create a new tour package'}
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#FF6B35] hover:bg-[#E85A2D]"
          data-testid="save-tour-btn"
        >
          {saving ? (
            <>
              <span className="animate-spin mr-2">⏳</span> Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" /> Save Tour
            </>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors",
              activeTab === tab.id 
                ? "bg-[#FF6B35] text-white" 
                : "bg-white text-[#4A4A4A] hover:bg-gray-100"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Tour Name *</Label>
                  <Input
                    id="name"
                    value={tour.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Enchanting Kerala Backwaters"
                    required
                    data-testid="tour-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    value={tour.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    placeholder="enchanting-kerala-backwaters"
                    data-testid="tour-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="destination">Destination *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="destination"
                      value={tour.destination}
                      onChange={(e) => handleChange('destination', e.target.value)}
                      placeholder="e.g., Kerala, India"
                      className="pl-10"
                      required
                      data-testid="tour-destination-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={tour.category} 
                    onValueChange={(v) => handleChange('category', v)}
                  >
                    <SelectTrigger data-testid="tour-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="short_description">Short Description</Label>
                <Textarea
                  id="short_description"
                  value={tour.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                  placeholder="Brief description for cards and listings..."
                  rows={2}
                  data-testid="tour-short-desc-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Full Description</Label>
                <Textarea
                  id="description"
                  value={tour.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Detailed tour description..."
                  rows={5}
                  data-testid="tour-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="duration_days">Duration (Days)</Label>
                  <Input
                    id="duration_days"
                    type="number"
                    min="1"
                    value={tour.duration_days}
                    onChange={(e) => handleChange('duration_days', parseInt(e.target.value) || 1)}
                    data-testid="tour-days-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration_nights">Duration (Nights)</Label>
                  <Input
                    id="duration_nights"
                    type="number"
                    min="0"
                    value={tour.duration_nights}
                    onChange={(e) => handleChange('duration_nights', parseInt(e.target.value) || 0)}
                    data-testid="tour-nights-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_group_size">Max Group Size</Label>
                  <Input
                    id="max_group_size"
                    type="number"
                    min="1"
                    value={tour.max_group_size}
                    onChange={(e) => handleChange('max_group_size', parseInt(e.target.value) || 1)}
                    data-testid="tour-group-size-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="featured"
                    checked={tour.featured}
                    onCheckedChange={(v) => handleChange('featured', v)}
                    data-testid="tour-featured-switch"
                  />
                  <Label htmlFor="featured">Featured Tour</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="status"
                    checked={tour.status === 'active'}
                    onCheckedChange={(v) => handleChange('status', v ? 'active' : 'inactive')}
                    data-testid="tour-status-switch"
                  />
                  <Label htmlFor="status">Active</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="price">Regular Price (₹) *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      value={tour.price}
                      onChange={(e) => handleChange('price', parseInt(e.target.value) || 0)}
                      className="pl-10"
                      required
                      data-testid="tour-price-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discounted_price">Discounted Price (₹)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="discounted_price"
                      type="number"
                      min="0"
                      value={tour.discounted_price || ''}
                      onChange={(e) => handleChange('discounted_price', e.target.value ? parseInt(e.target.value) : null)}
                      className="pl-10"
                      placeholder="Leave empty if no discount"
                      data-testid="tour-discounted-price-input"
                    />
                  </div>
                </div>
              </div>
              {tour.discounted_price && tour.price > 0 && (
                <p className="text-sm text-green-600">
                  Discount: {Math.round(((tour.price - tour.discounted_price) / tour.price) * 100)}% off
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Highlights */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Highlights
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem('highlights')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tour.highlights.map((highlight, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={highlight}
                      onChange={(e) => handleArrayChange('highlights', index, e.target.value)}
                      placeholder={`Highlight ${index + 1}`}
                    />
                    {tour.highlights.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeArrayItem('highlights', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Inclusions */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Inclusions
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem('inclusions')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tour.inclusions.map((inclusion, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={inclusion}
                      onChange={(e) => handleArrayChange('inclusions', index, e.target.value)}
                      placeholder={`e.g., Hotel accommodation`}
                    />
                    {tour.inclusions.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeArrayItem('inclusions', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Exclusions */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Exclusions
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem('exclusions')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tour.exclusions.map((exclusion, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={exclusion}
                      onChange={(e) => handleArrayChange('exclusions', index, e.target.value)}
                      placeholder={`e.g., Flight tickets`}
                    />
                    {tour.exclusions.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeArrayItem('exclusions', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Itinerary Tab */}
        {activeTab === 'itinerary' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Day-by-Day Itinerary
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addItineraryDay}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Day
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {tour.itinerary.map((day, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-[#004E64]">Day {day.day}</h4>
                    {tour.itinerary.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeItineraryDay(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={day.title}
                      onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                      placeholder="e.g., Arrival in Kerala"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={day.description}
                      onChange={(e) => handleItineraryChange(index, 'description', e.target.value)}
                      placeholder="Describe the day's activities..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Accommodation</Label>
                    <Input
                      value={day.accommodation}
                      onChange={(e) => handleItineraryChange(index, 'accommodation', e.target.value)}
                      placeholder="e.g., 4-star resort in Munnar"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Tour Images</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center mb-6">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label 
                  htmlFor="image-upload"
                  className="cursor-pointer"
                >
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-[#4A4A4A] mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-gray-400">
                    PNG, JPG, WEBP up to 5MB each
                  </p>
                </label>
              </div>

              {/* Image Grid */}
              {tour.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {tour.images.map((image, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={image}
                        alt={`Tour image ${index + 1}`}
                        className="w-full h-full object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-2 left-2 px-2 py-1 bg-[#FF6B35] text-white text-xs rounded-full">
                          Cover
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Image URL Input */}
              <div className="mt-6 space-y-2">
                <Label>Or add image URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="image-url"
                    placeholder="https://example.com/image.jpg"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const url = document.getElementById('image-url').value;
                      if (url) {
                        setTour(prev => ({
                          ...prev,
                          images: [...prev.images, url]
                        }));
                        document.getElementById('image-url').value = '';
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
}
